import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { clientApi } from '../../services/api';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusTimeline from '../../components/common/StatusTimeline';
import PhotoThumb from '../../components/common/PhotoThumb';
import NoteThread from '../../components/common/NoteThread';
import AuditLogPanel from '../../components/common/AuditLogPanel';
import { selectCompanyName } from '../../store/slices/authSlice';
import { format } from 'date-fns';
import { downloadBomComponentsExcel } from '../../utils/bomExcelExport';
// ── AI Summary — plug-and-play (remove this import to hide the panel) ────────
import AiSummaryPanel from '../../features/ai-summary/AiSummaryPanel';

// ── Small helper sub-components ──────────────────────────────────────────────
function Th({ children, w, required }) {
  return (
    <th style={w ? { width: w, minWidth: w } : { minWidth: '120px' }}
      className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
      {children}{required && <span className="text-red-500 ml-0.5">*</span>}
    </th>
  );
}

/** Build an editable draft from a single BOM part */
const CATEGORY_OPTIONS = [
  { group: 'Fabric & Cores', items: ['Casing', 'Full Cushion', 'Core', 'Toss Pillow', 'Back Pillow', 'Leather'] },
  { group: 'Wood', items: ['Drawers', 'Panels', 'Full Wood Chair', 'Wood Legs', 'Console & Lid'] },
  { group: 'Electronics', items: [
    'Motors/Actuator', 'Massage Motor', 'Switches', 'Hand Wand', 'Control Pad',
    'Wireless Charger', 'USB Charger', 'Transformer', 'Cup Holder', 'Control Box',
    'Remote Control', 'Kit - Control Box/Remote', 'Compressor', 'Airbag', 'Y-Cable',
    'Extension Cable', 'Power Cable', 'Other Cable', 'Power Outlet', 'Fuse', 'Socket',
    'Reading Light', 'Speakers', 'LED Light', 'Sensor Buttons', 'Battery', 'Other Electronics',
  ]},
  { group: 'Mechanisms', items: [
    'Mechanism Scissors', 'Sleeper - Queen', 'Sleeper - Full', 'Sleeper - Twin',
    'Mechanism Tubes', 'Mechanism Springs', 'Headrest Mechanism', 'Lumbar Mechanism',
    'Headrest/Lumbar Mechanism Kit', 'Mechanism Hardware', 'Mechanism Base', 'Other Mechanisms',
  ]},
  { group: 'Hardware', items: [
    'Pull Handle', 'Storage Hinge/Mech', 'Bracket', 'Side Glides', 'Underneath Glide',
    'Headrest Latch', 'Seat Springs', 'Soft Close Piston', 'Caster Wheel',
    'Drawer/Door Handle', 'Drawer/Door Knob', 'Rubber Coaster', 'Footrest Plastic/Metal Support',
    'Power Motion Connector', 'Stationary Connector', 'Tray Cover - Plastic',
    'Tray Cover with Wireless Charger', 'Electronic Hardware', 'Heating Pad',
    'Pole / Rod / Stand', 'Metal Sofa Legs', 'Plastic Sofa Legs', 'Other Hardware',
  ]},
];

const CATEGORY_VALUES = CATEGORY_OPTIONS.flatMap(({ group, items }) =>
  items.map(item => ({ value: `${group} > ${item}`, item }))
);

export function normalizeMeasurementUnit(value) {
  const unit = String(value ?? '').trim().toUpperCase();
  if (unit === 'CM' || unit === 'CENTIMETER' || unit === 'CENTIMETERS') return 'CM';
  return 'IN';
}

export function normalizeWeightUnit(value) {
  const unit = String(value ?? '').trim().toLowerCase();
  if (unit === 'lb' || unit.includes('pound')) return 'lb';
  if (unit === 'oz' || unit.includes('ounce')) return 'oz';
  if (unit === 'kg' || unit.includes('kilogram')) return 'kg';
  if (unit === 'g' || unit.includes('gram')) return 'g';
  return unit;
}

export function normalizeCategory(value) {
  const category = String(value ?? '').trim();
  if (!category) return '';
  const comparable = category.toUpperCase();
  return CATEGORY_VALUES.find(({ value: option, item }) =>
    option.toUpperCase() === comparable || item.toUpperCase() === comparable
  )?.value || category;
}

export function buildDraft(p) {
  return {
    id:                      p.id,
    partName:                p.partName              ?? p.articleName ?? '',
    partDescription:         p.articleName           ?? p.description ?? '',
    stockType:               p.stockType              ?? 'STOCK',
    partColor:               p.partColor              ?? '',
    width:                   p.width                  ?? (p.measurement ?? ''),
    length:                  p.length                 ?? '',
    height:                  p.height                 ?? '',
    measurementUnit:         normalizeMeasurementUnit(p.measurementUnit),
    partWeight:              p.partWeight             ?? '',
    weightUnit:              normalizeWeightUnit(p.weightUnit),
    category:                normalizeCategory(p.category),
    vendorPartNumber:        p.vendorPartNumber        ?? '',
    vendorFactoryName:       p.vendorFactoryName       ?? '',
    supplierName:            p.supplierName            ?? '',
    supplierPartNumber:      p.supplierPartNumber      ?? '',
    uom:                     p.uom                     ?? '',
    totalQtyInFg:            p.totalQtyInFg            ?? '',
    costPerUom:              p.unitPrice               ?? '',
    currency:                p.currency                ?? 'USD',
  };
}

export function buildBomUpdatePayload(row, assignmentId) {
  const optionalNumber = (value) => String(value ?? '').trim() === '' ? null : Number(value);
  return {
    assignmentId,
    components: [{
      id:                     row.id,
      partName:               row.partName,
      partDescription:        row.partDescription,
      stockType:              row.stockType,
      partColor:              row.partColor,
      width:                  row.width,
      length:                 row.length,
      height:                 row.height,
      measurementUnit:        row.measurementUnit,
      partWeight:             row.partWeight,
      weightUnit:             row.weightUnit,
      category:               row.category,
      vendorPartNumber:       row.vendorPartNumber,
      vendorFactoryName:      row.vendorFactoryName,
      supplierName:           row.supplierName,
      supplierPartNumber:     row.supplierPartNumber,
      uom:                    row.uom,
      totalQtyInFg:           optionalNumber(row.totalQtyInFg),
      costPerUom:             optionalNumber(row.costPerUom),
      currency:               row.currency,
    }],
  };
}

const CELL_INPUT  = 'w-full px-2 py-1 border border-blue-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-800';
const CELL_SELECT = 'w-full px-2 py-1 border border-blue-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-400 text-gray-800';
const ERR_INPUT   = 'w-full px-2 py-1 border border-red-400 rounded text-xs bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-400 text-gray-800';
const ERR_SELECT  = 'w-full px-2 py-1 border border-red-400 rounded text-xs bg-red-50 focus:outline-none focus:ring-1 focus:ring-red-400 text-gray-800';
const isNumericDimension = (value) => /^\d+(\.\d+)?$/.test(String(value ?? '').trim());

export function isRowValid(row) {
  const s = (v) => String(v ?? '').trim();
  return !!(
    s(row.partName) &&
    s(row.partDescription) &&
    s(row.partColor) &&
    [row.length, row.width, row.height].some(isNumericDimension) &&
    [row.length, row.width, row.height].every(value => !s(value) || isNumericDimension(value)) &&
    ['IN', 'CM'].includes(row.measurementUnit) &&
    s(row.partWeight) &&
    row.weightUnit &&
    s(row.category) &&
    s(row.vendorPartNumber) &&
    s(row.supplierName) &&
    s(String(row.totalQtyInFg)) &&
    row.uom
  );
}

export function InlineEditRow({ row, idx, onChange, onSave, onDiscard, isSaving }) {
  const f = (field) => (e) => onChange(field, e.target.value);
  const s = (v) => String(v ?? '').trim();

  const ic = (field) => s(row[field]) ? CELL_INPUT : ERR_INPUT;
  const sc = (field) => row[field] ? CELL_SELECT : ERR_SELECT;
  const dimErr = !s(row.width) && !s(row.length) && !s(row.height);
  const dc = (field) => (dimErr && !s(row[field])) ? ERR_INPUT : CELL_INPUT;
  const valid = isRowValid(row);
  const hasKnownCategory = CATEGORY_VALUES.some(({ value }) => value === row.category);

  return (
    <tr className="align-top bg-blue-50/40 border-l-2 border-blue-400">
      <td className="px-3 py-2 text-gray-400 text-xs">{idx + 1}</td>
      <td className="px-2 py-2 min-w-[140px]"><input className={ic('partName')} value={row.partName} onChange={f('partName')} placeholder="Part name" /></td>
      <td className="px-2 py-2 min-w-[180px]"><input className={ic('partDescription')} value={row.partDescription} onChange={f('partDescription')} placeholder="Description" /></td>
      <td className="px-2 py-2 min-w-[100px]"><input className={ic('partColor')} value={row.partColor} onChange={f('partColor')} /></td>
      <td className="px-2 py-2 min-w-[90px]"><select className={sc('measurementUnit')} value={row.measurementUnit} onChange={f('measurementUnit')}><option value="IN">IN</option><option value="CM">CM</option></select></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('length')} value={row.length} onChange={f('length')} placeholder="L" /></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('width')}  value={row.width}  onChange={f('width')}  placeholder="W" /></td>
      <td className="px-2 py-2 min-w-[80px]"><input type="number" min="0" step="any" className={dc('height')} value={row.height} onChange={f('height')} placeholder="H" /></td>
      <td className="px-2 py-2 min-w-[110px]">
        <select className={sc('weightUnit')} value={row.weightUnit} onChange={f('weightUnit')}>
          <option value="">Unit…</option>
          <option value="lb">Pounds (lb)</option>
          <option value="oz">Ounces (oz)</option>
          <option value="kg">Kilograms (kg)</option>
          <option value="g">Grams (g)</option>
        </select>
      </td>
      <td className="px-2 py-2 min-w-[90px]"><input className={ic('partWeight')} value={row.partWeight} onChange={f('partWeight')} /></td>
      <td className="px-2 py-2 min-w-[180px]">
        <select className={sc('category')} value={row.category} onChange={f('category')}>
          <option value="">SELECT CATEGORY…</option>
          {!hasKnownCategory && row.category && (
            <option value={row.category}>{row.category.toUpperCase()}</option>
          )}
          {CATEGORY_OPTIONS.map(({ group, items }) => (
            <optgroup key={group} label={group.toUpperCase()}>
              {items.map(item => (
                <option key={`${group}-${item}`} value={`${group} > ${item}`}>{item.toUpperCase()}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </td>
      <td className="px-2 py-2 min-w-[120px]"><input className={ic('vendorPartNumber')} value={row.vendorPartNumber} onChange={f('vendorPartNumber')} /></td>
      <td className="px-2 py-2 min-w-[120px]"><input className={ic('supplierName')} value={row.supplierName} onChange={f('supplierName')} /></td>
      <td className="px-2 py-2 min-w-[80px]"><input className={s(String(row.totalQtyInFg)) ? CELL_INPUT : ERR_INPUT} type="number" value={row.totalQtyInFg} onChange={f('totalQtyInFg')} placeholder="Qty" /></td>
      <td className="px-2 py-2 min-w-[100px]">
        <select className={sc('uom')} value={row.uom} onChange={f('uom')}>
          <option value="">UoM…</option>
          {['EACH','SET','PCS','PAIR','BOX','ROLL','MTR','KG','LTR'].map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </td>
      <td className="px-2 py-2">
        <div className="w-10 h-10 rounded border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-gray-300 text-xs">—</div>
      </td>
      <td className="px-2 py-2 min-w-[110px]">
        <div className="flex flex-col gap-1">
          <button
            onClick={onSave}
            disabled={isSaving || !valid}
            title={!valid ? 'Fill all required fields' : undefined}
            className="px-2.5 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium">
            {isSaving ? 'Saving…' : '💾 Save'}
          </button>
          <button
            onClick={onDiscard}
            disabled={isSaving}
            className="px-2.5 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            Discard
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function ClientReview() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const qc           = useQueryClient();
  const companyName  = useSelector(selectCompanyName);
  const [notes, setNotes]       = useState('');
  const [decision, setDecision] = useState(null); // 'approve' | 'reject'

  // ── BOM per-row inline-edit state ─────────────────────────────────────
  const [editingRowId, setEditingRowId] = useState(null);
  const [draftRow,     setDraftRow]     = useState(null);
  // ── Delete confirmation state ──────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const { data, isLoading } = useQuery(
    ['client-fg', id],
    () => clientApi.getFinishedGood(id),
    { refetchOnWindowFocus: false }
  );
  const fg = data?.data;

  // Identify the OmniDrive-approved assignment so we can fetch its full BOM
  // Also include CANCELLED so the BOM and audit log remain visible for cancelled processes
  const approvedAsgnId = fg?.assignments?.find(a =>
    ['COMPANY_APPROVED', 'CLIENT_APPROVED', 'CLIENT_REJECTED', 'CVA_REJECTED', 'PENDING_FINAL_APPROVAL', 'CANCELLED'].includes(a.status)
  )?.assignmentId ?? null;

  // Any assignment (for notes — visible from ASSIGNED onward)
  const anyAsgnId = fg?.assignments?.[0]?.assignmentId ?? null;

  const { data: asgnData, isLoading: asgnLoading } = useQuery(
    ['client-assignment', approvedAsgnId],
    () => clientApi.getAssignment(approvedAsgnId),
    { enabled: !!approvedAsgnId, refetchOnWindowFocus: false }
  );
  const fullAssignment = asgnData?.data;

  const bomParts = fullAssignment?.partArticles?.filter(p => !p.mainProduct) ?? [];
  const canDownloadSubmittedBom = Boolean(fullAssignment?.submittedAt && bomParts.length > 0);

  const handleDownloadBom = () => {
    try {
      downloadBomComponentsExcel({
        components: bomParts,
        finishedGoodCode: fullAssignment?.bobFinishedGoodCode || fg?.fgNumber || fg?.fgName,
      });
      toast.success('BOM downloaded successfully.');
    } catch {
      toast.error('Could not download the BOM Excel file. Please try again.');
    }
  };

  const reviewMutation = useMutation(clientApi.clientReview, {
    onSuccess: (_, vars) => {
      toast.success(vars.approved ? '✅ Request approved!' : '❌ Request rejected.');
      qc.invalidateQueries(['client-fg', id]);
      qc.invalidateQueries('client-requests');
      setDecision(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Review failed'),
  });

  const updateBomMutation = useMutation(
    (payload) => clientApi.updateBom(approvedAsgnId, payload),
    {
      onSuccess: (data) => {
        toast.success('BOM row saved.');
        qc.setQueryData(['client-assignment', approvedAsgnId], data);
        setEditingRowId(null);
        setDraftRow(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Save failed'),
    }
  );

  const deleteBomMutation = useMutation(
    (componentId) => clientApi.deleteBomComponent(approvedAsgnId, componentId),
    {
      onSuccess: (data) => {
        toast.success('BOM component deleted.');
        qc.setQueryData(['client-assignment', approvedAsgnId], data);
        setDeleteConfirmId(null);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Delete failed'),
    }
  );

  const handleStartEdit = (part) => {
    setEditingRowId(part.id);
    setDraftRow(buildDraft(part));
    setDeleteConfirmId(null);
  };

  const handleDiscard = () => {
    setEditingRowId(null);
    setDraftRow(null);
  };

  const handleSaveRow = () => {
    updateBomMutation.mutate(buildBomUpdatePayload(draftRow, approvedAsgnId));
  };

  const setDraftField = (field, value) =>
    setDraftRow((prev) => ({ ...prev, [field]: value }));

  const handleReview = (approved) => {
    const assignmentId = fg?.assignments?.find(a => a.status === 'COMPANY_APPROVED')?.assignmentId
                      || fg?.assignments?.[0]?.assignmentId;
    if (!assignmentId) return toast.error('No approved assignment found');
    reviewMutation.mutate({ assignmentId, approved, notes });
  };

  if (isLoading) return <LoadingSpinner />;
  if (!fg) return (
    <div className="text-center py-16">
      <p className="text-gray-400">Request not found or not accessible.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-sm text-blue-600">← Go back</button>
    </div>
  );

  // canReview: account can approve/reject only at COMPANY_APPROVED — their approval moves it to PENDING_FINAL_APPROVAL
  // canEdit:   BOM is editable at both stages (until CVA gives final approval at CLIENT_APPROVED)
  const canReview = fg.status === 'COMPANY_APPROVED';
  const canEdit   = (fg.status === 'COMPANY_APPROVED' || fg.status === 'PENDING_FINAL_APPROVAL') && !!approvedAsgnId;
  const isEditing = editingRowId !== null;

  return (
    <div className="w-full pb-10 space-y-6">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div>
        <button onClick={() => navigate(-1)} className="text-sm text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1">
          ← Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{fg.fgName}</h1>
            <p className="text-sm text-gray-400 font-mono mt-0.5">
              {fg.fgNumber}
              {fg.collectionName && <> · {fg.collectionName}</>}
            </p>
          </div>
          <StatusBadge status={fg.status} />
        </div>
      </div>

      {/* ── Cancelled Banner ─────────────────────────────────────────────── */}
      {fg.status === 'CANCELLED' && (
        <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
          <svg className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-gray-700">This process has been cancelled</p>
            <p className="text-xs text-gray-500 mt-0.5">No further action can be taken. The submission and history are preserved below for reference.</p>
          </div>
        </div>
      )}

      {/* ── Lifecycle Timeline ────────────────────────────────────────────── */}
      {fg.assignments?.length > 0 && (() => {
        const performerAliases = {};
        fg.assignments.forEach(a => {
          if (a.vendorEmail && a.vendorName) performerAliases[a.vendorEmail] = a.vendorName;
        });
        return (
          <StatusTimeline
            assignments={fg.assignments}
            fgCreatedAt={fg.createdAt}
            fgClientApprovedAt={fg.clientApprovedAt}
            fgStatus={fg.status}
            assignmentId={approvedAsgnId}
            fetchHistory={clientApi.getAssignmentHistory}
            performerAliases={performerAliases}
            companyLabel="OmniDrive"
            clientLabel={companyName || 'Account'}
            hideHistory={true}
          />
        );
      })()}

      {/* ── Request Details ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="font-semibold text-gray-900 mb-4">Request Details</h2>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            ['Category',     fg.category],
            ['Top Category', fg.topCategory],
            ['Quantity',     `${fg.quantity ?? '—'} ${fg.unitOfMeasure ?? ''}`],
            ['Collection',   fg.collectionName],
            ['Country',      fg.countryOfOrigin],
            ['Created',      fg.createdAt ? format(new Date(fg.createdAt), 'dd MMM yyyy') : '—'],
          ].map(([k, v]) => (
            <div key={k}>
              <dt className="text-xs text-gray-500 uppercase tracking-wide">{k}</dt>
              <dd className="mt-0.5 text-sm font-medium text-gray-900">{v || '—'}</dd>
            </div>
          ))}
        </dl>
        {fg.description && (
          <div className="mt-4 bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase mb-1">Description</p>
            <p className="text-sm text-gray-700">{fg.description}</p>
          </div>
        )}
      </div>

      {/* ── Vendor Submission + Full BOM ──────────────────────────────────── */}
      {approvedAsgnId && (
        asgnLoading ? (
          <LoadingSpinner message="Loading BOM..." />
        ) : fullAssignment ? (() => {
          const mainArticle = fullAssignment.partArticles?.find(p => p.mainProduct);
          return (
            <>
              {/* ── AI Summary Panel ── */}
              <AiSummaryPanel assignmentId={approvedAsgnId} />

              {/* ── Key Dates ── */}
              <LifecycleDates assignment={fullAssignment} />

              {/* ── Finished Good Details (main product) ── */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-lg">📦</span>
                  <h2 className="text-lg font-bold text-gray-900">Finished Good Details</h2>
                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">main product</span>
                  <div className="ml-auto">
                    <StatusBadge status={fullAssignment.status} />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                  {[
                    ['Product Title',                  fullAssignment.requestTitle || fullAssignment.fgName],
                    ['Finished Good Code',             fullAssignment.bobFinishedGoodCode],
                    ['Country of Origin',              fullAssignment.countryOfOrigin],
                    ['Top Category',                   fullAssignment.topCategory],
                    ['Category',                       fullAssignment.category],
                    ['Collection Name',                fullAssignment.collectionName],
                    ['Finished Product',               fullAssignment.finishedProduct],
                    ['Vendor Finished Good Model',     fullAssignment.vendorFinishedGoodModel],
                    ['Factory Code',                   fullAssignment.factoryCode],
                    ['Factory Name',                   fullAssignment.factoryName],
                    ['Finish',                         fullAssignment.finish],
                    ['Color',                          fullAssignment.color],
                    ['Vendor Product Code',            fullAssignment.vendorProductCode],
                    ['Package Sales Set',              fullAssignment.packageSalesSet],
                    ['Package Sales Set Name',         fullAssignment.packageSalesSetName],
                    ['Package Component Qty',          fullAssignment.packageComponentQty],
                    ['Vendor Code (Internal to Bobs)', fullAssignment.vendorCode],
                    ['Vendor Name',                    fullAssignment.fgVendorName],
                  ].map(([label, value]) => (
                    <div key={label}>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm min-h-[36px] break-words">
                        {value != null && value !== ''
                          ? <span className={label === 'Vendor Code (Internal to Bobs)' ? 'text-orange-600 font-medium' : 'text-gray-700'}>{value}</span>
                          : <span className="text-gray-400">—</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {fullAssignment.productDescription && (
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Product Description</label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 min-h-[36px] break-words">
                      {fullAssignment.productDescription}
                    </div>
                  </div>
                )}

                {/* ── Vendor-Submitted Main Product Info ── */}
                {mainArticle && (
                  <div className="border-t border-gray-100 pt-4 mt-2 mb-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Vendor-Submitted Main Product Info</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                      {[
                        ['FG Article ID',       mainArticle?.fgArticleId],
                        ['Collection',          mainArticle?.collectionName || fullAssignment.collectionName],
                        ['Product Description', mainArticle?.productDescription || fullAssignment.productDescription],
                        ['Article Group',       mainArticle?.articleGroup],
                        ['Style / Generic',     mainArticle?.genericStyle],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</label>
                          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 min-h-[36px] break-words">
                            {value || <span className="text-gray-400">—</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* ── Main Product Photo ── */}
                <div className="mt-2 pt-5 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-3">Main Product Photo</p>
                  {mainArticle?.files?.length > 0 ? (
                    <div className="flex gap-3 flex-wrap">
                      {mainArticle.files.map((f, i) => (
                        <PhotoThumb key={i} fileId={f.id} label={f.originalName} />
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-28 h-28 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50">
                      <div className="text-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
                          className="h-7 w-7 text-gray-300 mx-auto mb-1">
                          <path d="M4 16l4-4a3 3 0 014 0l4 4M14 14l1-1a3 3 0 014 0l1 1M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="text-xs text-gray-400">No photo</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── BOM Components Table ── */}
              {bomParts.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <span className="text-base">🔩</span>
                    <h2 className="font-semibold text-gray-900">BOM Components</h2>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{bomParts.length} parts</span>
                    <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                      {canDownloadSubmittedBom && (
                        <button
                          onClick={handleDownloadBom}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors">
                          📥 Download BOM
                        </button>
                      )}
                      {canEdit && isEditing && (
                        <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-medium">
                          ✏️ Editing row
                        </span>
                      )}
                    </div>
                  </div>

                  {canEdit && isEditing && (
                    <div className="mx-5 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center justify-between">
                      <span>Edit the fields inline, then click <strong>Save</strong> on that row. Click <strong>Discard</strong> to cancel.</span>
                      <span className="ml-4 whitespace-nowrap text-red-500 font-bold">* All fields are mandatory</span>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          <Th w="40px">#</Th>
                          <Th required w="140px">Part</Th>
                          <Th required w="180px">Description</Th>
                          <Th required>Part Color</Th>
                          <Th required w="90px">Measurement Unit</Th>
                          <Th required w="75px">L</Th>
                          <Th required w="75px">W</Th>
                          <Th required w="75px">H</Th>
                          <Th required w="130px">Weight Unit</Th>
                          <Th required>Weight</Th>
                          <Th required w="200px">Category</Th>
                          <Th required>Vendor Part No.</Th>
                          <Th required>Supplier</Th>
                          <Th required w="80px">Qty</Th>
                          <Th required w="100px">UoM</Th>
                          <Th w="140px">Photo</Th>
                          {canEdit && <Th w="110px">Actions</Th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {bomParts.map((part, idx) => {
                          const isThisRowEditing = editingRowId === part.id;
                          const otherRowEditing  = isEditing && !isThisRowEditing;
                          const photos           = part.files ?? [];
                          const isPendingDel     = deleteConfirmId === part.id;
                          const otherPendingDel  = deleteConfirmId !== null && deleteConfirmId !== part.id;

                          if (isThisRowEditing && draftRow) {
                            return (
                              <InlineEditRow
                                key={part.id}
                                row={draftRow}
                                idx={idx}
                                onChange={setDraftField}
                                onSave={handleSaveRow}
                                onDiscard={handleDiscard}
                                isSaving={updateBomMutation.isLoading}
                              />
                            );
                          }

                          return (
                            <tr key={part.id} className={`hover:bg-gray-50 transition-colors ${isPendingDel ? 'bg-red-50/40' : ''} ${otherRowEditing ? 'opacity-50' : ''}`}>
                              <td className="px-3 py-3 text-gray-400 text-xs">{idx + 1}</td>
                              <td className="px-3 py-3 font-medium text-gray-900">
                                {part.partName || part.articleName || '—'}
                              </td>
                              <td className="px-3 py-3 text-gray-700">
                                {part.articleName || part.description || '—'}
                              </td>
                              <td className="px-3 py-3 text-gray-700">{part.partColor || '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.measurementUnit || 'IN'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.length || '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.width || '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.height || '—'}</td>
                              <td className="px-3 py-3 text-gray-700 text-xs">{part.weightUnit ? {'lb':'Pounds (lb)','oz':'Ounces (oz)','kg':'Kilograms (kg)','g':'Grams (g)'}[part.weightUnit] || part.weightUnit : '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.partWeight || '—'}</td>
                              <td className="px-3 py-3 text-gray-600 text-xs">{part.category || '—'}</td>
                              <td className="px-3 py-3 font-mono text-xs text-blue-600">{part.vendorPartNumber || '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.supplierName || '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.totalQtyInFg != null ? part.totalQtyInFg : '—'}</td>
                              <td className="px-3 py-3 text-gray-700">{part.uom || '—'}</td>
                              <td className="px-3 py-3">
                                {photos.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {photos.map((f, i) => (
                                      <PhotoThumb key={i} fileId={f.id} label={f.originalName} small />
                                    ))}
                                  </div>
                                ) : (
                                  <div className="w-12 h-12 rounded-lg border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-5 w-5 text-gray-300">
                                      <path d="M4 16l4-4a3 3 0 014 0l4 4M14 14l1-1a3 3 0 014 0l1 1M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                )}
                              </td>
                              {canEdit && (
                                <td className="px-3 py-3">
                                  {isPendingDel ? (
                                    <div className="flex flex-col gap-1">
                                      <span className="text-xs text-red-600 font-medium mb-0.5">Delete?</span>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => deleteBomMutation.mutate(part.id)}
                                          disabled={deleteBomMutation.isLoading}
                                          className="px-2 py-1 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors font-medium">
                                          {deleteBomMutation.isLoading ? '…' : 'Yes'}
                                        </button>
                                        <button
                                          onClick={() => setDeleteConfirmId(null)}
                                          disabled={deleteBomMutation.isLoading}
                                          className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
                                          No
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col gap-1">
                                      <button
                                        onClick={otherRowEditing || otherPendingDel ? undefined : () => handleStartEdit(part)}
                                        disabled={otherRowEditing || otherPendingDel}
                                        title="Edit this row"
                                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors
                                          ${otherRowEditing || otherPendingDel
                                            ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                                            : 'border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-400'}`}>
                                        ✏️ Edit
                                      </button>
                                      <button
                                        onClick={otherRowEditing || otherPendingDel ? undefined : () => setDeleteConfirmId(part.id)}
                                        disabled={otherRowEditing || otherPendingDel}
                                        title="Delete this BOM row"
                                        className={`flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border transition-colors
                                          ${otherRowEditing || otherPendingDel
                                            ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                                            : 'border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400'}`}>
                                        🗑 Delete
                                      </button>
                                    </div>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          );
        })() : null
      )}

      {/* ── Notes thread ── */}
      {anyAsgnId && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Notes
            <span className="text-xs text-gray-400 font-normal">— visible to OmniDrive, vendor &amp; account</span>
          </h3>
          <NoteThread
            assignmentId={anyAsgnId}
            fetchNotes={clientApi.getAssignmentNotes}
            postNote={clientApi.addAssignmentNote}
            canPost={fg.status !== 'CANCELLED'}
            queryKey="client-notes"
          />
        </div>
      )}

      {/* ── Audit Log ──────────────────────────────────────────────────────── */}
      {approvedAsgnId && (
        <AuditLogPanel
          assignmentId={approvedAsgnId}
          fetchHistory={clientApi.getAssignmentHistory}
          queryKey="client-audit-log"
        />
      )}

      {/* ── Client Review Action ──────────────────────────────────────────── */}
      {canReview && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-1">Your Decision</h2>
          <p className="text-sm text-gray-500 mb-4">
            {isEditing
              ? 'Save or discard your BOM row edit above before approving or rejecting.'
              : 'OmniDrive has approved this submission. Review the BOM and approve or reject it.'}
          </p>

          {decision && !isEditing ? (
            <div className={`rounded-lg border p-4 mb-4 ${decision === 'approve' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm font-medium mb-2 ${decision === 'approve' ? 'text-green-800' : 'text-red-800'}`}>
                {decision === 'approve' ? '✅ Approving this submission' : '❌ Rejecting this submission'}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Add notes or reason..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <div className="mt-3 flex gap-3">
                <button
                  onClick={() => handleReview(decision === 'approve')}
                  disabled={reviewMutation.isLoading}
                  className={`px-5 py-2 text-sm text-white rounded-lg disabled:opacity-50 font-medium ${
                    decision === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {reviewMutation.isLoading ? 'Processing...' : decision === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                </button>
                <button
                  onClick={() => { setDecision(null); setNotes(''); }}
                  disabled={reviewMutation.isLoading}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setDecision('reject')}
                disabled={isEditing}
                title={isEditing ? 'Save or discard your BOM edit first' : undefined}
                className="px-4 py-2 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Reject
              </button>
              <button
                onClick={() => setDecision('approve')}
                disabled={isEditing}
                title={isEditing ? 'Save or discard your BOM edit first' : undefined}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Approve
              </button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

// ── Lifecycle Dates panel ────────────────────────────────────────────────────

function LifecycleDates({ assignment }) {
  const fmt = (d) => d ? format(new Date(d), 'dd MMM yyyy, hh:mm a') : '—';
  const reviewLabel = () => {
    if (!assignment.reviewedAt) return '—';
    if (['COMPANY_APPROVED', 'PENDING_FINAL_APPROVAL', 'CLIENT_APPROVED'].includes(assignment.status)) return `Approved · ${fmt(assignment.reviewedAt)}`;
    if (['COMPANY_REJECTED', 'CLIENT_REJECTED', 'CVA_REJECTED'].includes(assignment.status))  return `Rejected · ${fmt(assignment.reviewedAt)}`;
    return fmt(assignment.reviewedAt);
  };

  const dates = [
    { icon: '🔄', label: 'FG Synced',          value: fmt(assignment.fgCreatedAt) },
    { icon: '📋', label: 'Assigned to Vendor',  value: fmt(assignment.assignedAt) },
    { icon: '📤', label: 'Vendor Submitted',     value: fmt(assignment.submittedAt) },
    { icon: '✅', label: 'OmniDrive Reviewed',   value: reviewLabel() },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Key Dates
      </h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {dates.map(({ icon, label, value }) => (
          <div key={label} className="flex flex-col gap-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{icon} {label}</span>
            <span className="text-sm text-gray-800 font-medium">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
